import { useState } from 'react';

import type {
  BroadcastContent,
  ConnectingDelayJudgementMode,
  DelayBroadcastPattern,
} from '../../../api/types';
import {
  PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST,
  PHRASE_TEMPLATE_CONNECTING_STATION_DIRECTION_PREFIX,
  PHRASE_TEMPLATE_CONNECTING_STATION_WHOLE_LINE_PREFIX,
} from '../../../data/phraseTemplates';
import {
  CONNECTING_STATION_GRANULARITY_DEFAULT_SSML,
  CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_SSML,
  getConnectingStationGranularityDefaults,
  isDepartureForecastMode,
} from '../../../utils/broadcastContents';
import type { ValidationIssue } from '../../../utils/validationIssues';
import { hasIssueForTestId } from '../../../utils/validationIssues';

import { createDefaultPattern, resolveSsmlTemplateOnGranularityChange } from '../logic';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import { resolveConnectingStationDirectionPlaceholders } from '../placeholders';
import { deriveContentSampleOverrides, deriveSampleDelaySeconds } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { DelayPatternRow } from './DelayPatternRow';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「遅延放送パターン表」。
 *
 * 発車見込み案内 (`departure_forecast` / SBI-5) では「放送粒度」列を隠す。
 * 粒度は文面に影響しないため保存時に固定値へ揃え (`pinDepartureForecastGranularity`)、
 * 方面部分文は粒度別ではなく単一のマスタ ID (`connecting_station_departure_forecast`) を引く。
 * 「遅れ分数」は「どの遅れのときに案内するか」を表す。
 *
 * 自列車遅延の `SelfTrainDelayFields` / part1 の `ConnectingLineDelayPatternsTable` と
 * 同じ `DelayPatternRow` を再利用し、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (方面部分文SSML) / 行操作 (＋−)」を表示する。
 *
 * ここで編集する `ssml_template` は **方面部分文** (`{directions_text}` に流し込まれる素材)
 * である点が part1 の transfer_aware 用テーブルと違う。placeholder / 凡例も part2 用に切り替える。
 *
 * デフォルト文言・凡例は **親接続線ブロックの判定モード** (`judgementMode`) で出し分ける。
 * `whole_line` (接続線全体の遅延) は方面の入力を求めないモードなので、
 * `{direction_name}` を含まない文面・凡例を使う (含めても空欄になり文が崩れる)。
 */
export function ConnectingStationLineDelayPatternsTable({
  lineIndex,
  patterns,
  judgementMode = 'whole_line',
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  issues = [],
  onChange,
}: {
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  patterns: DelayBroadcastPattern[];
  /**
   * この接続線に紐づく入力エラー (親で `lineIndex` 絞り込み済み)。
   * どの行 (パターン) が原因かを行単位で赤く示すために使う。
   */
  issues?: ValidationIssue[];

  /**
   * 親接続線ブロックの判定モード。
   * デフォルト文言 / placeholder / 凡例の出し分けに使う (既定は `whole_line`)。
   */
  judgementMode?: ConnectingDelayJudgementMode;
  /** プレビュー対象の放送コンテンツ (Issue #439) */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  onChange: (nextPatterns: DelayBroadcastPattern[]) => void;
}): React.JSX.Element {
  /*
   * プレビューで採用された遅延パターン行の index (Issue #439)。
   * サーバの `select_delay_pattern()` の結果をそのまま反映してハイライトする。
   */
  const [matchedPatternIndex, setMatchedPatternIndex] = useState<number | null>(null);
  // 判定モードに応じたデフォルト方面部分文・凡例 (whole_line は {direction_name} なし)。
  const isDepartureForecast = isDepartureForecastMode(judgementMode);
  const granularityDefaults = getConnectingStationGranularityDefaults(judgementMode);
  const directionPlaceholders = resolveConnectingStationDirectionPlaceholders(judgementMode);
  // 空配列の場合は最低 1 行を表示 (part1 と同じポリシー)。
  const rows: DelayBroadcastPattern[] =
    patterns.length > 0 ? patterns : [createDefaultPattern(granularityDefaults)];

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = rows.slice();
    const current = next[index]!;
    // Issue #527: 粒度が変わっても **デフォルト文を実値で書き込まない**
    // (空欄 = 定型文マスタへ委譲。放送側が判定モードに応じて
    //  `connecting_station_direction_{粒度}` / `connecting_station_whole_line_{粒度}` を引く)。
    // 未編集 (空欄 or いずれかのモード・粒度のデフォルト文と一致) なら空欄に保つ。
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        // 未編集判定は **両モードのデフォルト文セット** と突き合わせる
        // (旧仕様ではモード切替のたびにどちらかが焼き込まれていたため)。
        ssml_template: resolveSsmlTemplateOnGranularityChange(
          current.ssml_template,
          CONNECTING_STATION_GRANULARITY_DEFAULT_SSML,
          CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_SSML,
        ),
        /*
         * Issue #556: 粒度を変えても言語別文面へ既定訳を **書き込まない**。
         *
         * 個別文面は定型文マスタより優先されるため、粒度を触っただけで
         * 「定型文マスタの多言語編集が以後反映されない」状態になっていた。
         * 未入力時に放送される文面は placeholder (薄字) で提示する。
         * 入力済みの訳文は温存する (`current.ssml_template_i18n` をそのまま維持)。
         */
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    onChange(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = rows.slice();
    next.splice(index + 1, 0, createDefaultPattern(granularityDefaults));
    onChange(next);
  };

  const removePattern = (index: number): void => {
    if (rows.length <= 1) return;
    const next = rows.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div
      className="connecting-station-line-delay-patterns"
      data-testid={`connecting-station-line-delay-patterns-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>遅延放送パターン (方面部分文 SSML)</div>
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            {isDepartureForecast ? null : (
              <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
                放送粒度
              </th>
            )}
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (方面部分文 SSML)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((pattern, i) => (
            <DelayPatternRow
              languages={content?.languages}
              key={i}
              index={i}
              pattern={pattern}
              canRemove={rows.length > 1}
              ssmlTestIdPrefix={`connecting-station-line-${lineIndex}-direction-ssml`}
              placeholderTemplates={granularityDefaults}
              templateKind={isDepartureForecast ? 'station_departure_forecast' : 'station'}
              // Issue #527: 空欄時の placeholder を定型文マスタの文面にする。
              // 判定モードで参照する ID が変わる (whole_line は方面に触れない文面)。
              // SBI-5: 発車見込み案内は粒度別ではなく単一のマスタ ID を引く。
              granularityTemplatePrefix={
                isDepartureForecast
                  ? undefined
                  : judgementMode === 'whole_line'
                    ? PHRASE_TEMPLATE_CONNECTING_STATION_WHOLE_LINE_PREFIX
                    : PHRASE_TEMPLATE_CONNECTING_STATION_DIRECTION_PREFIX
              }
              fixedTemplateId={
                isDepartureForecast
                  ? PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST
                  : undefined
              }
              hideGranularity={isDepartureForecast}
              // Issue #439: 行ごとに「▶ この文面を試聴」を出す。
              // part2 は方面部分文が「冒頭文 + ブロック文」に埋め込まれる三層構造なので、
              // サーバはコンテンツ全体をレンダリングして返す (= 本番と同じ完成形が鳴る)。
              previewSlot={
                content ? (
                  <AudioPreviewField
                    content={content}
                    parentVoiceId={parentVoiceId}
                    playbackRate={playbackRate}
                    onPreviewSsml={onPreviewSsml}
                    testIdSuffix={`connecting-station-${contentIndex}-${lineIndex}-${i}`}
                    label="▶ この文面を試聴"
                    placeholders={directionPlaceholders}
                    // 乗換駅 / 線名 / 方面はコンテンツ設定から導出し、
                    // 遅延分数だけはこの行が選ばれる値で上書きする
                    // (条件パネルで手入力した値はそのまま尊重される)。
                    sampleOverrides={{
                      ...deriveContentSampleOverrides(content),
                      delaySeconds: deriveSampleDelaySeconds(pattern),
                    }}
                    // 発車見込み案内 (SBI-6) も試聴できる。時刻 / 行先はプレビュー条件パネルで指定する。
                    disabled={(pattern.ssml_template ?? '').trim() === ''}
                    disabledReason="SSML を入力すると試聴できます"
                    onMatchedPatternIndexChange={setMatchedPatternIndex}
                  />
                ) : null
              }
              highlighted={matchedPatternIndex === i}
              // 行単位のエラー表示。バリデーション側が付ける testId
              // (`connecting-station-line-{lineIndex}-direction-ssml-{i}`) と
              // 同じ規則で突き合わせる。
              invalid={hasIssueForTestId(
                issues,
                `connecting-station-line-${lineIndex}-direction-ssml-${i}`,
              )}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>
      <p className="hint" style={{ margin: '8px 0 4px' }}>
        遅れ分数の幅ごとに「方面部分文 SSML」を設定します。この文言が各方面ごとに展開され、
        読点で連結されて接続線ブロックの <code>{'{directions_text}'}</code> に埋め込まれます。
      </p>
      {isDepartureForecast ? (
        <p
          className="hint"
          style={{ margin: '4px 0' }}
          data-testid={`connecting-station-line-departure-forecast-hint-${lineIndex}`}
        >
          「発車の見込み」では遅延分数を読み上げません。「遅れ分数」は
          <strong>どれだけ遅れているときに発車見込みを案内するか</strong>
          を表します (該当する行が無い方面は案内しません)。定時 (遅れ 0 分) も案内したい場合は「0
          分以上」の行にしてください。方面部分文は「{'{headsign}'}は{'{departure_time}'}頃」までで、
          語尾は接続線ブロック SSML テンプレートが担います。
        </p>
      ) : null}
    </div>
  );
}
